<template>
    <AbstractMenuBar :show-branch-selector="true"
                     :show-connectivity="true"
                     :show-update="true">
        <template v-slot:left>
            <template v-if="loggedIn && deviceSet">
                <a v-if="viewPrivileges.sell"
                   class="btn btn-dark-blue"
                   data-analytics-id="Menu Bar / Sell"
                   :class="{ active: currentPageIsSell }"
                   href="#/sell">
                    <i class="fa-solid fa-cart-shopping"></i>
                    <span class="text">{{ l('NAV.SELL') }}</span>
                </a>
                <a v-if="viewPrivileges.redeem"
                   class="btn btn-dark-blue"
                   data-analytics-id="Menu Bar / Redeem (Orders)"
                   :class="{ active: currentPageIsRedeem }"
                   href="#/orders">
                    <i class="fa-solid fa-circle-check"></i>
                    <span class="text">{{ l('NAV.REDEEM') }}</span>
                </a>
                <a v-if="viewPrivileges.accounts"
                   class="btn btn-dark-blue"
                   data-analytics-id="Menu Bar / Accounts"
                   :class="{ active: currentPageIsManage }"
                   href="#/accounts">
                    <i class="fa-solid fa-user"></i>
                    <span class="text">{{ l('NAV.ACCOUNTS') }}</span>
                </a>
                <a v-if="viewPrivileges.today"
                   class="btn btn-dark-blue"
                   data-analytics-id="Menu Bar / Today"
                   :class="{ active: currentPageIsToday }"
                   href="#/today">
                    <i class="fa-solid fa-calendar"></i>
                    <span class="text">{{ l('NAV.TODAY') }}</span>
                </a>
                <div v-if="showSearchModalButton"
                     class="desktop-only btn btn-dark-blue"
                     data-analytics-id="Menu Bar / Search Modal"
                     @click="showSearch">
                    <i class="fa-solid fa-search"></i>
                    <span class="text">{{ l('CUSTOMER_DETAILS.SEARCH') }}</span>
                </div>
            </template>
            <template v-else-if="loggedIn && !deviceSet">
                <a class="btn btn-dark-blue"
                   data-analytics-id="Menu Bar / Device"
                   :class="{ active: currentPageIsDevice }"
                   href="#/device">
                    <i class="fa-solid fa-cash-register"></i>
                    <span class="text">{{ l('NAV.DEVICE') }}</span>
                </a>
            </template>
            <template v-else-if="!loggedIn">
                <a class="btn btn-dark-blue"
                   data-analytics-id="Menu Bar / Login"
                   :class="{ active: currentPageIsLogin }"
                   href="#/login">
                    <i class="fa-solid fa-lock"></i>
                    <span class="text">{{ l('NAV.LOGIN') }}</span>
                </a>
            </template>
        </template>

        <template v-slot:right>
            <MenuBarBranch v-if="loggedIn"
                           @click="showMenu" />

            <MenuButton v-if="loggedIn"
                        @click="showMenu" />
        </template>
    </AbstractMenuBar>
</template>

<script>
import AbstractMenuBar from '@/vue/components/Menus/AbstractMenuBar';
import EventNames from '@/lib/Events/EventNames';
import MenuBarBranch from '@/vue/components/Menus/MenuBarBranch';
import MenuButton from '@/vue/components/Menus/MenuButton';
import PaymentMethodRef from '@/lib/PaymentMethods/PaymentMethodRef';
import { container } from '@/container';
import { mapGetters, mapState } from 'vuex';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';

export default {
    name: 'EposMenuBar',

    components: {
        MenuBarBranch,
        MenuButton,
        AbstractMenuBar,
    },

    data() {
        return {
            foodCount: null,
            productsCount: null,
            vouchersCount: null,
            tescoClubcard: null,
            deregisterItemsSyncedListenerFunc: null,
        };
    },

    computed: {
        ...mapState([
            'currentPageHash',
            'viewPrivileges',
        ]),

        ...mapState('cartOffers', [
            'couponsCount',
        ]),

        ...mapGetters([
            'loggedIn',
            'deviceSet',
        ]),

        currentPageIsLogin() {
            return this.currentPageHash.indexOf('#/login') === 0;
        },

        currentPageIsDevice() {
            return this.currentPageHash.indexOf('#/device') === 0;
        },

        currentPageIsSell() {
            return this.currentPageHash.indexOf('#/sell') === 0;
        },

        currentPageIsRedeem() {
            return this.currentPageHash.indexOf('#/orders') === 0;
        },

        currentPageIsManage() {
            return this.currentPageHash.indexOf('#/accounts') === 0;
        },

        currentPageIsReport() {
            return this.currentPageHash.indexOf('#/report') === 0;
        },

        currentPageIsToday() {
            return this.currentPageHash.indexOf('#/today') === 0;
        },

        showSearchModalButton() {
            const hasCoupons = this.couponsCount > 0;
            const hasFood = this.foodCount > 0;
            const hasProducts = this.productsCount > 0;
            const hasVouchers = this.vouchersCount > 0;
            const hasClubcard = !!this.tescoClubcard;

            console.log(`Coupons: ${hasCoupons}, Clubcard: ${hasClubcard}, Food: ${hasFood}, Products: ${hasProducts}, Vouchers: ${hasVouchers}`);

            return (hasCoupons || hasClubcard || hasFood || hasProducts || hasVouchers)
                && (this.deviceSet && this.viewPrivileges.sell);
        },
    },

    created() {
        this.deregisterItemsSyncedListenerFunc = container.$rootScope.$on(EventNames.ITEMS_SYNCED, () => {
            this.initializeSearchData();
            this.setCouponsCount();
        });

        this.initializeSearchData();
        this.setCouponsCount();
    },

    beforeUnmount() {
        if (this.deregisterItemsSyncedListenerFunc) {
            this.deregisterItemsSyncedListenerFunc();
        }
    },

    methods: {
        showMenu() {
            container.vueModalFactory.openAndIgnoreResult(MODAL_NAMES__EPOS.EPOS_MENU);
        },
        showSearch() {
            container.vueModalFactory.openAndIgnoreResult(MODAL_NAMES__EPOS.SEARCH);
        },

        async initializeSearchData() {
            try {
                const getAllSearchData = await container.saleableItemService.itemTypes;

                this.foodCount = getAllSearchData?.Food?.count || 0;
                this.productsCount = getAllSearchData?.Product?.count || 0;
                this.vouchersCount = getAllSearchData?.GiftVouchers?.count || 0;
                this.tescoClubcard = container.paymentMethodService.getByRef(PaymentMethodRef.TESCO_VOUCHER_UK);
            } catch (error) {
                console.error('Error initializing search modal:', error);
                this.foodCount = 0;
                this.productsCount = 0;
                this.vouchersCount = 0;
                this.tescoClubcard = null;
            }
        },

        async setCouponsCount() {
            try {
                const allCouponsOrderOffers = await container.offerRepository.countAllCouponOrderOffers();
                this.$store.commit('cartOffers/setCouponsCount', allCouponsOrderOffers);
                await this.$nextTick();
            } catch (error) {
                console.error('Error fetching coupon offers:', error);
            }
        },
    },
};
</script>
